import { createModal, StaticModal } from '@nexelem/modals';
import { useCustomTranslation } from '../../../../../hooks';
import { Button, Checkbox } from '@nexelem/components';
import { FormProvider, useForm } from 'react-hook-form';
import { FormCalendarInput } from '@nexelem/form-components';
import { ShiftSelector } from '../../../ShiftSelector';
import { AreaSelect } from './AreaSelect';
import { FormControlLabel } from '@nexelem/deprecated-theme';
import { Shift, useConfig } from '@nexelem/config';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUnassignAllWorkersMutation } from './useUnassignAllWorkersMutation';
import { useState } from 'react';
import { getShiftDateRange } from './getShiftDateRange';

const unassignWorkersFormSchema = z.object({
  date: z.date(),
  onlyUnavailable: z.boolean(),
  queueAreaId: z.number(),
});

type UnassignWorkersForm = z.infer<typeof unassignWorkersFormSchema>;

const AutoUnassignmentModal = () => {
  const shifts = useConfig().modules.production.workerModeViewShifts;
  const { t, tWrapper } = useCustomTranslation('products.productionPlan.autoAssigment');
  const unassignWorkers = useUnassignAllWorkersMutation();
  const [selectedShifts, setSelectedShifts] = useState<Shift['nameKey'][]>([]);

  const form = useForm<UnassignWorkersForm>({
    resolver: zodResolver(unassignWorkersFormSchema),
  });

  const handleSubmit = (values: UnassignWorkersForm) => {
    const dateRange = getShiftDateRange(shifts, selectedShifts, values.date);

    if (!dateRange) {
      return;
    }

    unassignWorkers.mutate({
      from: dateRange.from.toISOString(),
      to: dateRange.to.toISOString(),
      onlyUnavailable: values.onlyUnavailable,
      queueAreaId: values.queueAreaId,
    });
  };

  return (
    <FormProvider {...form}>
      <StaticModal
        component="form"
        maxWidth="lg"
        onClose={closeAutoUnassigmentModal}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <StaticModal.Header withCloseButton>{tWrapper('unassignManyHeader')}</StaticModal.Header>
        <StaticModal.Content className="flex flex-col gap-6">
          <p className="text-s18">{tWrapper('unassignManyInfo')}</p>
          <AreaSelect />
          <FormCalendarInput required className="max-w-56" label={t('common.date')} name="date" />
          <ShiftSelector selectedShifts={selectedShifts} setSelectedShifts={setSelectedShifts} />
          <FormControlLabel
            {...form.register('onlyUnavailable')}
            className="ml-2"
            control={<Checkbox />}
            label={<span className="ml-2 text-s16 font-normal">{tWrapper('unassignOnlyUnavailable')}</span>}
          />
        </StaticModal.Content>
        <StaticModal.Footer>
          <Button variant="outlined" onClick={closeAutoUnassigmentModal}>
            {t('common.cancel')}
          </Button>
          <Button isLoading={unassignWorkers.isPending} type="submit" variant="contained">
            {tWrapper('unassignWorkers')}
          </Button>
        </StaticModal.Footer>
      </StaticModal>
    </FormProvider>
  );
};

export const { openAutoUnassigmentModal, closeAutoUnassigmentModal } = createModal(AutoUnassignmentModal, {
  name: 'AutoUnassigmentModal',
});
